/// <reference types="vite/client" />

import React, { useState, useEffect } from 'react';
import { useNavigation } from '../navigation';
import InventoryStats from './Inventory-stats';
import { sendAdminRequest } from './request-button';

// Utility function to get auth token with debugging
const getAuthToken = () => {
  const token = localStorage.getItem('authToken');
  console.log('=== Auth Token Debug ===');
  console.log('Token exists:', !!token);
  console.log('Token length:', token ? token.length : 0);
  console.log('Token preview:', token ? token.substring(0, 20) + '...' : 'No token');
  return token;
};

const API_BASE = import.meta.env.DEV ? '/api' : 'https://pharma-amd-0-1-42.onrender.com/pharma/api';

interface Medication {
  id: number;
  medicine_name: string;
  medicine_company: string;
  medicine_composition: string;
  batch_no: string;
  expiry_date: string;
  quantity: number;
  quantity_type: string;
  raw_quantity_type?: string;
  tablets_per_strip: number;
  fragmented_tablets?: number;
  total_tablets: number;
  unit_cost: number;
  selling_price: number;
  mrp: number;
  storage_location: string;
  is_expired: boolean;
  days_to_expiry: number;
  created_at: string;
  updated_at: string;
  hsn_code?: string;
  gst_percent?: number;
}

interface EditFormData {
  medicine_name: string;
  medicine_company: string;
  medicine_composition: string;
  batch_no: string;
  expiry_date: string;
  quantity: number;
  quantity_type: string;
  tablets_per_strip: number | '';
  fragmented_tablets: number | '';
  unit_cost: number;
  mrp: number;
  selling_price: number;
  hsn_code: string;
  storage_location: string;
  gst_percent: number;
}

interface InventoryProps {
  showHeader?: boolean;
}

const allowedQtyTypeValues = [
  'individual',
  'strip',
  'bottle',
  'sachet',
  'pouch',
  'vial',
  'ampoule',
  'tube',
  'box',
  'pack'
] as const;

const allowedQtyTypeSet = new Set<string>(allowedQtyTypeValues);

const normalizeQtyTypeToApi = (value: any): string => {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return 'individual';
  if (allowedQtyTypeSet.has(raw)) return raw;

  if (raw === 'strip' || raw === 'strips') return 'strip';
  if (raw === 'bottle' || raw === 'bottles') return 'bottle';
  if (raw === 'tablet' || raw === 'tablets' || raw === 'tab' || raw === 'tabs') return 'individual';
  if (raw === 'capsule' || raw === 'capsules' || raw === 'cap' || raw === 'caps') return 'individual';
  if (raw === 'syrup' || raw === 'syrups' || raw === 'syp') return 'bottle';
  if (raw === 'injection' || raw === 'injections' || raw === 'inj') return 'vial';
  if (raw === 'box' || raw === 'boxes') return 'box';
  if (raw === 'tube' || raw === 'tubes') return 'tube';
  if (raw === 'amp' || raw === 'ampules' || raw === 'ampoules') return 'ampoule';
  if (raw === 'sachet' || raw === 'sachets') return 'sachet';
  if (raw === 'pouch' || raw === 'pouches') return 'pouch';
  if (raw === 'pack' || raw === 'packs') return 'pack';

  if (raw === 'lotion' || raw === 'lotions') return 'bottle';
  if (raw === 'gel' || raw === 'gels') return 'tube';
  if (raw === 'cream' || raw === 'creams') return 'tube';
  if (raw === 'powder' || raw === 'power') return 'pack';

  return 'individual';
};

const qtyTypeOptions: Array<{ value: string; label: string }> = [
  { value: 'individual', label: 'Individual / Tablet' },
  { value: 'strip', label: 'Strip' },
  { value: 'bottle', label: 'Bottle' },
  { value: 'sachet', label: 'Sachet' },
  { value: 'pouch', label: 'Pouch' },
  { value: 'vial', label: 'Vial' },
  { value: 'ampoule', label: 'Ampoule' },
  { value: 'tube', label: 'Tube' },
  { value: 'box', label: 'Box' },
  { value: 'pack', label: 'Pack' }
];

const toFiniteNumber = (value: any, fallback: number): number => {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const parseNumericValue = (value: any, fallback: number): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;
  if (typeof value !== 'string') return fallback;
  const sanitized = value.replace(/[^0-9.-]/g, '');
  const parsed = parseFloat(sanitized);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const firstDefined = (...values: any[]) => values.find(v => v !== undefined && v !== null);

const formatQtyTypeLabel = (qtyType: any): string => {
  const normalized = normalizeQtyTypeToApi(qtyType);
  return qtyTypeOptions.find(o => o.value === normalized)?.label || normalized;
};

const formatQtyTypeFromApi = (medication: Medication): string => {
  const raw = medication.raw_quantity_type;
  const normalized = normalizeQtyTypeToApi(raw ?? medication.quantity_type);
  const label = qtyTypeOptions.find(o => o.value === normalized)?.label;
  const rawNormalized = raw != null ? normalizeQtyTypeToApi(raw) : normalized;
  if (label && raw != null && rawNormalized !== normalized) return `${label} (${normalized}, from: ${raw})`;
  if (label && raw != null && String(raw).trim() !== normalized) return `${label} (${normalized}, from: ${raw})`;
  if (label) return `${label} (${normalized})`;
  return raw != null ? String(raw) : normalized;
};

const normalizeDateToIso = (value: any): string => {
  const str = String(value ?? '').trim();
  if (!str) return '';
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const dmyDash = str.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmyDash) return `${dmyDash[3]}-${dmyDash[2]}-${dmyDash[1]}`;
  const dmySlash = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmySlash) return `${dmySlash[3]}-${dmySlash[2]}-${dmySlash[1]}`;
  return str.length >= 10 ? str.slice(0, 10) : str;
};

// Function to process medication data and ensure proper formatting
const processMedicationData = (medication: any): Medication => {
  // If medicine_name is a JSON string, try to parse it
  if (typeof medication.medicine_name === 'string' && medication.medicine_name.startsWith('{')) {
    try {
      const parsedData = JSON.parse(medication.medicine_name);
      const quantityTypeRaw = firstDefined(
        parsedData.quantity_type,
        parsedData.quantityType,
        parsedData.qty_type,
        parsedData.quantity_unit,
        parsedData.quantityUnit,
        parsedData.unit,
        parsedData.unit_type,
        parsedData.unitType,
        medication.quantity_type,
        medication.quantityType,
        medication.qty_type,
        medication.quantity_unit,
        medication.quantityUnit,
        medication.unit,
        medication.unit_type,
        medication.unitType
      );
      const quantityType = normalizeQtyTypeToApi(quantityTypeRaw);

      const quantityRaw = firstDefined(parsedData.quantity, medication.quantity);
      const quantity = parseNumericValue(quantityRaw, 0);

      const tabletsPerStripRaw = firstDefined(parsedData.tablets_per_strip, parsedData.tabletsPerStrip, medication.tablets_per_strip, medication.tabletsPerStrip);
      const tabletsPerStrip = parseNumericValue(tabletsPerStripRaw, 1);

      const fragmentedTabletsRaw = firstDefined(parsedData.fragmented_tablets, parsedData.fragmentedTablets, medication.fragmented_tablets, medication.fragmentedTablets);
      const fragmentedTablets = parseNumericValue(fragmentedTabletsRaw, 0);

      const totalTabletsRaw = firstDefined(parsedData.total_tablets, parsedData.totalTablets, medication.total_tablets, medication.totalTablets);
      const computedTotalTablets = quantityType === 'strip' ? (quantity * Math.max(1, tabletsPerStrip) + Math.max(0, fragmentedTablets)) : quantity;
      const totalTablets = parseNumericValue(totalTabletsRaw, computedTotalTablets);

      const unitCost = parseNumericValue(firstDefined(parsedData.cost_price_per_unit, parsedData.unit_cost, medication.unit_cost), 0);
      const sellingPrice = parseNumericValue(firstDefined(parsedData.selling_price_per_unit, parsedData.selling_price, medication.selling_price), 0);
      const mrpRaw = parseNumericValue(firstDefined(parsedData.mrp, medication.mrp), 0);
      const mrp = mrpRaw > 0 ? mrpRaw : (sellingPrice > 0 ? sellingPrice : 0);
      const gstPercent = parseNumericValue(firstDefined(parsedData.gst_percent, parsedData.gstPercent, medication.gst_percent, medication.gstPercent), 0);

      return {
        ...medication,
        medicine_name: parsedData.medicine_name || parsedData.name || 'Unknown Medicine',
        medicine_company: parsedData.medicine_company || parsedData.company || parsedData.manufacturer || 'Unknown Company',
        medicine_composition: parsedData.medicine_composition || parsedData.composition || parsedData.salt_composition || 'Unknown Composition',
        batch_no: String(parsedData.batch_no || parsedData.batch_number || 'N/A'),
        expiry_date: normalizeDateToIso(firstDefined(parsedData.expiry_date, parsedData.exp_date, parsedData.expiry, medication.expiry_date, medication.expiry)),
        quantity,
        quantity_type: quantityType,
        raw_quantity_type: quantityTypeRaw != null ? String(quantityTypeRaw) : undefined,
        tablets_per_strip: tabletsPerStrip,
        fragmented_tablets: fragmentedTablets,
        total_tablets: totalTablets,
        unit_cost: unitCost,
        selling_price: sellingPrice,
        mrp,
        storage_location: parsedData.storage_location || medication.storage_location || 'N/A',
        hsn_code: firstDefined(parsedData.hsn_code, medication.hsn_code) != null ? String(firstDefined(parsedData.hsn_code, medication.hsn_code)) : undefined,
        gst_percent: gstPercent
      };
    } catch (error) {
      console.error('Error parsing JSON medicine data:', error);
      return {
        ...medication,
        medicine_name: 'Invalid Data',
        medicine_company: 'Unknown Company',
        medicine_composition: 'Unknown Composition'
      };
    }
  }
  
  // Return the medication as-is if it's already properly formatted
  const quantityTypeRaw = firstDefined(
    medication.quantity_type,
    medication.quantityType,
    medication.qty_type,
    medication.type,
    medication.quantity_unit,
    medication.quantityUnit,
    medication.unit,
    medication.unit_type,
    medication.unitType
  );
  const quantityType = normalizeQtyTypeToApi(quantityTypeRaw);
  const quantity = parseNumericValue(medication.quantity, 0);
  const tabletsPerStrip = parseNumericValue(firstDefined(medication.tablets_per_strip, medication.tabletsPerStrip), 1);
  const fragmentedTablets = parseNumericValue(firstDefined(medication.fragmented_tablets, medication.fragmentedTablets), 0);
  const computedTotalTablets = quantityType === 'strip' ? (quantity * Math.max(1, tabletsPerStrip) + Math.max(0, fragmentedTablets)) : quantity;
  const totalTablets = parseNumericValue(firstDefined(medication.total_tablets, medication.totalTablets), computedTotalTablets);
  const unitCost = parseNumericValue(firstDefined(medication.cost_price_per_unit, medication.cost_price, medication.unit_cost, medication.unitCost, medication.cost), 0);
  const sellingPrice = parseNumericValue(firstDefined(medication.selling_price_per_unit, medication.selling_price, medication.sellingPrice, medication.sale_price, medication.price), 0);
  const mrpRaw = parseNumericValue(firstDefined(medication.mrp, medication.MRP), 0);
  const mrp = mrpRaw > 0 ? mrpRaw : (sellingPrice > 0 ? sellingPrice : 0);
  const gstPercent = parseNumericValue(firstDefined(medication.gst_percent, medication.gstPercent, medication.gst), 0);
  const hsnCodeValue = firstDefined(medication.hsn_code, medication.hsnCode, medication.hsn);
  const medicineNameValue = firstDefined(medication.medicine_name, medication.name, medication.medicineName, medication.title);
  const medicineCompanyValue = firstDefined(medication.medicine_company, medication.company, medication.manufacturer, medication.brand);
  const medicineCompositionValue = firstDefined(medication.medicine_composition, medication.composition, medication.salt_composition, medication.saltComposition, medication.salt);
  const batchNoValue = firstDefined(medication.batch_no, medication.batchNo, medication.batch, medication.batch_number);
  const expiryDateValue = firstDefined(medication.expiry_date, medication.expiryDate, medication.exp_date, medication.expDate, medication.expiry);
  const storageLocationValue = firstDefined(medication.storage_location, medication.storageLocation, medication.location, medication.rack);

  return {
    ...medication,
    medicine_name: medicineNameValue != null ? String(medicineNameValue) : 'Unknown Medicine',
    medicine_company: medicineCompanyValue != null ? String(medicineCompanyValue) : 'Unknown Company',
    medicine_composition: medicineCompositionValue != null ? String(medicineCompositionValue) : 'Unknown Composition',
    batch_no: String(batchNoValue || 'N/A'),
    expiry_date: normalizeDateToIso(expiryDateValue),
    quantity,
    quantity_type: quantityType,
    raw_quantity_type: quantityTypeRaw != null ? String(quantityTypeRaw) : undefined,
    tablets_per_strip: tabletsPerStrip,
    fragmented_tablets: fragmentedTablets,
    total_tablets: totalTablets,
    unit_cost: unitCost,
    selling_price: sellingPrice,
    mrp,
    storage_location: storageLocationValue != null && String(storageLocationValue).trim() ? String(storageLocationValue) : 'N/A',
    hsn_code: hsnCodeValue != null ? String(hsnCodeValue) : undefined,
    gst_percent: gstPercent
  };
};

const Inventory: React.FC<InventoryProps> = ({ showHeader = true }) => {
  const { navigateTo } = useNavigation();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [medications, setMedications] = useState<Medication[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [apiError, setApiError] = useState('');
  const [pagination, setPagination] = useState<{ page: number; per_page: number; total: number; pages: number }>({
    page: 1,
    per_page: 10,
    total: 0,
    pages: 1
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [editingItem, setEditingItem] = useState<Medication | null>(null);
  const [editForm, setEditForm] = useState<EditFormData>({
    medicine_name: '',
    medicine_company: '',
    medicine_composition: '',
    batch_no: '',
    expiry_date: '',
    quantity: 0,
    quantity_type: 'individual',
    tablets_per_strip: '',
    fragmented_tablets: '',
    unit_cost: 0,
    mrp: 0,
    selling_price: 0,
    hsn_code: '',
    storage_location: '',
    gst_percent: 0
  });
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [isLoadingEditDetails, setIsLoadingEditDetails] = useState(false);
  const [editError, setEditError] = useState('');
  const [sellingPriceManuallyEdited, setSellingPriceManuallyEdited] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<Medication | null>(null);
  const [entityType, setEntityType] = useState<string>('');
  const isDoctorAssociated = entityType === 'doctor_associated';
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error'; visible: boolean } | null>(null);

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (notification?.visible) {
      const timer = setTimeout(() => {
        setNotification(prev => prev ? { ...prev, visible: false } : null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification?.visible]);

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ message, type, visible: true });
  };

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('pharmaEntityType');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (typeof parsed?.entity_type === 'string') {
        setEntityType(parsed.entity_type);
      }
    } catch {
      // ignore parse errors
    }
  }, []);

  // Fetch inventory data from API
  const fetchInventoryData = async (page: number = 1, perPage: number = pagination.per_page || 10, searchQuery?: string) => {
    try {
      setIsLoadingData(true);
      setApiError('');
      
      // Get auth token from localStorage
      const authToken = getAuthToken();
      
      if (!authToken) {
        console.warn('⚠️ No auth token found in localStorage. API call may fail.');
        setApiError('Authentication token not found. Please log in again.');
        setIsLoadingData(false);
        return;
      }
      
      console.log('Making API call to inventory endpoint...');
      console.log('Using token:', authToken ? 'Yes' : 'No');
      
      let url;
      if (searchQuery && searchQuery.trim()) {
        // Use search API when there's a search query
        url = `https://pharma-amd-0-1-42.onrender.com/pharma/api/inventory/search?q=${encodeURIComponent(searchQuery.trim())}&limit=${perPage}`;
      } else {
        // Use regular list API when no search query
        url = `https://pharma-amd-0-1-42.onrender.com/pharma/api/inventory/get-inventory/list?per_page=${perPage}&page=${page}`;
      }
      
      console.log('Request URL:', url);
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });
      
      console.log('API response received:', response.status);
      console.log('Response URL:', response.url);
      console.log('Was redirected:', response.redirected);

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP error! status: ${response.status}`);
      }

      const json = await response.json();
      console.log('Inventory data received:', json);
      
      // Extract items array from common response shapes
      let items = [];
      if (searchQuery) {
        // For search API, look for inventory array
        items = Array.isArray(json.inventory) ? json.inventory : [];
      } else {
        // For regular list API, use existing logic
        items = Array.isArray(json)
          ? json
          : Array.isArray(json.data)
            ? json.data
            : Array.isArray(json.items)
              ? json.items
              : Array.isArray(json.records)
                ? json.records
                : [];
      }
      
      // Extract pagination from response (only for non-search API calls)
      if (!searchQuery) {
        const paginationInfo = json.pagination || (json.meta && json.meta.pagination);
        if (paginationInfo) {
          const total = Number(paginationInfo.total) || items.length || 0;
          const pages = Number(paginationInfo.pages) || Math.ceil(total / Math.max(1, perPage)) || 1;
          const pageNum = Number(paginationInfo.page) || page || 1;
          setPagination({ page: pageNum, per_page: perPage, total, pages });
        } else {
          setPagination(prev => ({ ...prev, page, per_page: perPage }));
        }
      } else {
        // For search results, reset pagination to show search results
        setPagination({
          page: 1,
          per_page: perPage,
          total: json.count || items.length,
          pages: 1
        });
      }
      
      // Validate data structure and process each medication
      if (items.length) {
        const processedData = items.map(processMedicationData);
        setMedications(processedData);
      } else {
        console.warn('API returned no array items:', json);
        setMedications([]);
      }
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('Error fetching inventory data:', err);
      console.error('Error type:', err.name);
      console.error('Error message:', err.message);
      
      // Check for different types of errors
      if (err.message.includes('CORS') || err.message.includes('Failed to fetch') || 
          err.message.includes('preflight request') || err.message.includes('ERR_FAILED')) {
        setApiError(`
          CORS Error: Unable to connect to the inventory API.
          
          Possible solutions:
          1. Check if your server is running
          2. Verify CORS is properly configured on the server
          3. Ensure the API endpoint is accessible
          4. Try refreshing the page
          
          Server endpoint: https://pharma-amd-0-1-42.onrender.com/pharma/api/inventory?per_page=10&page=1
        `);
      } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        setApiError('Authentication failed. Please log in again.');
        // Clear invalid token
        // Don't auto-remove token - let user manually logout
      } else if (err.message.includes('403') || err.message.includes('Forbidden')) {
        setApiError('Access denied. You do not have permission to view inventory data.');
      } else {
        setApiError(`Failed to load inventory data: ${err.message}`);
      }
      
      // Fallback to sample data if API fails
      console.log('Falling back to sample data due to API error');
      setMedications(sampleInventoryData);
    } finally {
      setIsLoadingData(false);
    }
  };

  // Sample inventory data (fallback)
  const sampleInventoryData: Medication[] = [
    {
      id: 1,
      medicine_name: 'Paracetamol 500mg',
      medicine_company: 'Crocin',
      medicine_composition: 'Paracetamol',
      batch_no: 'B001',
      expiry_date: '2025-12-31',
      quantity: 150,
      quantity_type: 'individual',
      tablets_per_strip: 1,
      total_tablets: 150,
      unit_cost: 15.00,
      selling_price: 20.00,
      mrp: 20.00,
      storage_location: 'Row 1',
      is_expired: false,
      days_to_expiry: 365,
      created_at: '2024-01-15T00:00:00',
      updated_at: '2024-01-15T00:00:00'
    },
    {
      id: 2,
      medicine_name: 'Amoxicillin 250mg',
      medicine_company: 'Amoxil',
      medicine_composition: 'Amoxicillin',
      batch_no: 'B002',
      expiry_date: '2025-06-30',
      quantity: 25,
      quantity_type: 'individual',
      tablets_per_strip: 1,
      total_tablets: 25,
      unit_cost: 45.00,
      selling_price: 60.00,
      mrp: 60.00,
      storage_location: 'Row 2',
      is_expired: false,
      days_to_expiry: 150,
      created_at: '2024-01-14T00:00:00',
      updated_at: '2024-01-14T00:00:00'
    }
  ];

  // Fetch data on component mount and when search term changes
  useEffect(() => {
    if (searchTerm.trim()) {
      fetchInventoryData(1, pagination.per_page || 10, searchTerm);
    } else {
      fetchInventoryData(1, pagination.per_page || 10);
    }
  }, [searchTerm]);

  // Initial fetch on component mount
  useEffect(() => {
    fetchInventoryData(1);
  }, []);

  const buildEditFormFromMedication = (medication: Medication): EditFormData => {
    const normalizedQuantityType = normalizeQtyTypeToApi(medication.raw_quantity_type ?? medication.quantity_type);
    const tabletsPerStrip = parseNumericValue(firstDefined((medication as any).tablets_per_strip, (medication as any).tabletsPerStrip), 0);
    const fragmentedTablets = parseNumericValue(firstDefined((medication as any).fragmented_tablets, (medication as any).fragmentedTablets), 0);
    const normalizedMrp = toFiniteNumber((medication as any).mrp, 0);
    const normalizedSellingPrice = toFiniteNumber((medication as any).selling_price, 0);
    const mrpForEdit = normalizedMrp > 0 ? normalizedMrp : (normalizedSellingPrice > 0 ? normalizedSellingPrice : 0);
    // Keep selling_price independent - if it's 0 or not set, it will auto-fill from MRP when focused
    const sellingPriceForEdit = normalizedSellingPrice > 0 ? normalizedSellingPrice : 0;
    return {
      medicine_name: medication.medicine_name || '',
      medicine_company: medication.medicine_company || '',
      medicine_composition: medication.medicine_composition || '',
      batch_no: medication.batch_no || '',
      expiry_date: medication.expiry_date ? normalizeDateToIso(medication.expiry_date) : '',
      quantity: medication.quantity ?? 0,
      quantity_type: normalizedQuantityType,
      tablets_per_strip: normalizedQuantityType === 'strip' && tabletsPerStrip > 0 ? tabletsPerStrip : '',
      fragmented_tablets: normalizedQuantityType === 'strip' ? fragmentedTablets : '',
      unit_cost: medication.unit_cost ?? 0,
      mrp: mrpForEdit,
      selling_price: sellingPriceForEdit,
      hsn_code: (medication as any).hsn_code || '',
      storage_location: medication.storage_location || '',
      gst_percent: (medication as any).gst_percent ?? 0
    };
  };

  const openEditModal = async (medication: Medication) => {
    setEditingItem(medication);
    setEditForm(buildEditFormFromMedication(medication));
    setSellingPriceManuallyEdited(false);
    setEditError('');
    setIsLoadingEditDetails(true);
    try {
      const authToken = getAuthToken();
      if (!authToken) {
        setEditError('Authentication token not found. Please log in again.');
        return;
      }
      const baseUrl = `https://pharma-amd-0-1-42.onrender.com/pharma/api/inventory/get-inventory/item/${medication.id}`;
      console.groupCollapsed('[Inventory Edit] GET details', baseUrl);
      const makeRequest = async (url: string) => {
        const res = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
            ...(authToken && { 'Authorization': `Bearer ${authToken}` })
          }
        });
        console.log('status:', res.status, 'redirected:', res.redirected, 'url:', res.url);
        return res;
      };

      let response = await makeRequest(baseUrl);
      if (response.status === 307 || response.status === 308) {
        response = await makeRequest(`${baseUrl}/`);
      }
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}`);
      }

      const detailJson = await response.json();
      console.log('detailJson keys:', detailJson && typeof detailJson === 'object' ? Object.keys(detailJson) : typeof detailJson);

      const detailCandidate = firstDefined(
        (detailJson as any)?.data,
        (detailJson as any)?.item,
        (detailJson as any)?.inventory,
        (detailJson as any)?.record,
        (detailJson as any)?.result,
        (detailJson as any)?.medicine,
        detailJson
      );
      const detailObject = Array.isArray((detailCandidate as any)?.items)
        ? (detailCandidate as any).items[0]
        : Array.isArray(detailCandidate)
          ? detailCandidate[0]
          : detailCandidate;

      const detailedMedication = processMedicationData(detailObject);
      console.log('parsed details:', {
        id: detailedMedication.id,
        medicine_name: detailedMedication.medicine_name,
        medicine_company: detailedMedication.medicine_company,
        batch_no: detailedMedication.batch_no,
        expiry_date: detailedMedication.expiry_date,
        quantity_type: detailedMedication.quantity_type,
        raw_quantity_type: detailedMedication.raw_quantity_type
      });
      setEditingItem(detailedMedication);
      setEditForm(buildEditFormFromMedication(detailedMedication));
    } catch (err: any) {
      setEditError(err?.message || 'Failed to load inventory details.');
    } finally {
      console.groupEnd();
      setIsLoadingEditDetails(false);
    }
  };

  const closeEditModal = () => {
    setEditingItem(null);
    setEditError('');
    setIsLoadingEditDetails(false);
  };

  const handleDeleteItem = async (medication: Medication) => {
    setDeleteConfirmItem(null); // close confirmation popup
    setDeletingId(medication.id);
    try {
      const authToken = getAuthToken();
      if (!authToken) {
        setApiError('Authentication token not found. Please log in again.');
        return;
      }
      const url = `https://pharma-amd-0-1-42.onrender.com/pharma/api/inventory/delete-inventory/${medication.id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        }
      });
      if (!response.ok) {
        const errText = await response.text();
        if (response.status === 401) throw new Error('Authentication failed. Please log in again.');
        if (response.status === 403) throw new Error('You do not have permission to delete this item.');
        if (response.status === 404) throw new Error('Item not found.');
        throw new Error(errText || `Failed to delete (${response.status}).`);
      }
      await fetchInventoryData(pagination.page, pagination.per_page, searchTerm.trim() || undefined);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete item.';
      setApiError(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditSubmit = async () => {
    if (!editingItem) return;
    setIsSubmittingEdit(true);
    setEditError('');
    try {
      const authToken = getAuthToken();
      if (!authToken) {
        setEditError('Authentication token not found. Please log in again.');
        setIsSubmittingEdit(false);
        return;
      }
      const normalizedQuantityType = normalizeQtyTypeToApi(editForm.quantity_type);
      if (!allowedQtyTypeSet.has(normalizedQuantityType)) {
        setEditError('Invalid quantity type selected.');
        setIsSubmittingEdit(false);
        return;
      }
      if (normalizedQuantityType === 'strip') {
        const tabletsPerStrip = editForm.tablets_per_strip === '' ? 0 : Number(editForm.tablets_per_strip);
        if (!Number.isFinite(tabletsPerStrip) || tabletsPerStrip < 1) {
          setEditError('Tablets Per Strip is required when quantity type is strip.');
          setIsSubmittingEdit(false);
          return;
        }
      }
      const url = `https://pharma-amd-0-1-42.onrender.com/pharma/api/inventory/edit-inventory/${editingItem.id}`;
      const originalQuantityType = normalizeQtyTypeToApi(editingItem.quantity_type);
      const tabletsPerStripToSend =
        normalizedQuantityType === 'strip' ? Number(editForm.tablets_per_strip) : 1;
      const fragmentedTabletsToSend =
        normalizedQuantityType === 'strip'
          ? (editForm.fragmented_tablets === '' ? 0 : Number(editForm.fragmented_tablets))
          : 0;
      const originalTotalTablets = toFiniteNumber((editingItem as any).total_tablets, 0);
      const quantityToSend =
        normalizedQuantityType !== 'strip' && originalQuantityType === 'strip' && originalTotalTablets > 0
          ? originalTotalTablets
          : Number(editForm.quantity) || 0;

      const mrpInput = Number(editForm.mrp) || 0;
      const sellingInput = Number(editForm.selling_price) || 0;
      const mrpToSend = mrpInput > 0 ? mrpInput : null;
      // Use selling_price as entered, or if empty/0, use MRP
      const sellingToSend = sellingInput > 0 ? sellingInput : (mrpInput > 0 ? mrpInput : null);
      const body = {
        medicine_name: editForm.medicine_name.trim(),
        medicine_company: editForm.medicine_company.trim(),
        medicine_composition: editForm.medicine_composition.trim(),
        batch_no: editForm.batch_no.trim() || null,
        expiry_date: editForm.expiry_date || null,
        quantity: quantityToSend,
        quantity_type: normalizedQuantityType,
        tablets_per_strip: tabletsPerStripToSend,
        fragmented_tablets: fragmentedTabletsToSend,
        unit_cost: Number(editForm.unit_cost) || 0,
        mrp: mrpToSend,
        selling_price: sellingToSend,
        hsn_code: editForm.hsn_code.trim() || null,
        storage_location: editForm.storage_location.trim() || null,
        gst_percent: Number(editForm.gst_percent) || 0
      };

      if (isDoctorAssociated) {
        const requestBody = {
          action: 'inventory_edit',
          reference_id: String(editingItem.id),
          payload: {
            inventory: {
              old_data: {
                medicine_name: editingItem.medicine_name,
                medicine_company: editingItem.medicine_company,
                medicine_composition: editingItem.medicine_composition,
                batch_no: editingItem.batch_no,
                expiry_date: editingItem.expiry_date,
                quantity: editingItem.quantity,
                quantity_type: normalizeQtyTypeToApi(editingItem.quantity_type),
                tablets_per_strip: editingItem.tablets_per_strip,
                fragmented_tablets: Number(editingItem.fragmented_tablets || 0),
                unit_cost: editingItem.unit_cost,
                mrp: editingItem.mrp,
                selling_price: editingItem.selling_price,
                hsn_code: editingItem.hsn_code || null,
                storage_location: editingItem.storage_location || null,
                gst_percent: Number(editingItem.gst_percent || 0),
              },
              new_data: {
                medicine_name: body.medicine_name,
                medicine_company: body.medicine_company,
                medicine_composition: body.medicine_composition,
                batch_no: body.batch_no,
                expiry_date: body.expiry_date,
                quantity: body.quantity,
                quantity_type: body.quantity_type,
                tablets_per_strip: body.tablets_per_strip,
                fragmented_tablets: body.fragmented_tablets,
                unit_cost: body.unit_cost,
                mrp: body.mrp,
                selling_price: body.selling_price,
                hsn_code: body.hsn_code,
                storage_location: body.storage_location,
                gst_percent: body.gst_percent,
              },
            },
          },
        };

        await sendAdminRequest(requestBody);

        showNotification('Request sent successfully! Pending admin approval.', 'success');

        closeEditModal();
        await fetchInventoryData();
        return;
      }
      console.groupCollapsed('[Inventory Edit] PUT', url);
      console.log('editingItem.id:', editingItem.id);
      console.log('original quantity_type:', originalQuantityType);
      console.log('new quantity_type:', normalizedQuantityType);
      console.log('original total_tablets:', originalTotalTablets);
      console.log('request body:', body);
      const response = await fetch(url, {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(body)
      });
      console.log('response status:', response.status);
      if (!response.ok) {
        const errText = await response.text();
        console.error('response not ok:', response.status, errText);
        
        // Handle specific error cases with user-friendly messages
        if (response.status === 405) {
          throw new Error('This operation is not supported by the server. Please contact support.');
        } else if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (response.status === 403) {
          throw new Error('You do not have permission to perform this action.');
        } else if (response.status === 404) {
          throw new Error('The inventory item was not found.');
        } else if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        } else {
          throw new Error(`Failed to update inventory (${response.status}). Please try again.`);
        }
      }
      const responseText = await response.text();
      console.log('response ok:', responseText);
      console.groupEnd();

      showNotification('Inventory updated successfully!', 'success');

      closeEditModal();
      await fetchInventoryData();
    } catch (err: any) {
      console.groupCollapsed('[Inventory Edit] Error');
      console.error(err);
      console.groupEnd();
      setEditError(err?.message || 'Failed to update inventory.');
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const getStatusColor = (medication: Medication) => {
    if (medication.is_expired) return '#dc2626';
    if (medication.quantity === 0) return '#ef4444';
    if (medication.days_to_expiry <= 30) return '#f97316';
    if (medication.quantity < 50) return '#f59e0b';
    return '#22c55e';
  };

  const getStatusIcon = (medication: Medication) => {
    if (medication.is_expired) return '⚠';
    if (medication.quantity === 0) return '✗';
    if (medication.days_to_expiry <= 30) return '⏰';
    if (medication.quantity < 50) return '⚠';
    return '✓';
  };

  const getStatusText = (medication: Medication) => {
    if (medication.is_expired) return 'expired';
    if (medication.quantity === 0) return 'out-of-stock';
    if (medication.days_to_expiry <= 30) return 'expiring-soon';
    if (medication.quantity < 50) return 'low-stock';
    return 'in-stock';
  };

  const filteredMedications = medications.filter(med => {
    const status = getStatusText(med);
    const matchesStatus = statusFilter === 'all' || status === statusFilter;
    
    return matchesStatus;
  });

  const sortedMedications = [...filteredMedications].sort((a, b) => {
    switch (sortBy) {
      case 'name': return a.medicine_name.localeCompare(b.medicine_name);
      case 'quantity': return b.quantity - a.quantity;
      case 'unit_cost': return b.unit_cost - a.unit_cost;
      case 'selling_price': return b.selling_price - a.selling_price;
      case 'mrp': return b.mrp - a.mrp;
      case 'expiry': return new Date(a.expiry_date).getTime() - new Date(b.expiry_date).getTime();
      case 'status': return getStatusText(a).localeCompare(getStatusText(b));
      default: return 0;
    }
  });

  const effectivePerPage = pagination.per_page || 10;
  const totalItems = pagination.total || 0;
  const totalPages = pagination.pages || 1;
  const currentPage = pagination.page || 1;
  const canPrevPage = currentPage > 1;
  const canNextPage = currentPage < totalPages;
  const showingFrom = totalItems > 0 ? Math.min(totalItems, (currentPage - 1) * effectivePerPage + 1) : 0;
  const showingTo = totalItems > 0 ? Math.min(totalItems, currentPage * effectivePerPage) : 0;

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = isMobile ? 3 : 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      fontFamily: '"Noto Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      {showHeader && (
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        borderBottom: '1px solid rgba(34, 197, 94, 0.2)',
        padding: isMobile ? '0.75rem 0.5rem' : '1rem',
        marginBottom: isMobile ? '0.5rem' : '1rem',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(20px)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: isMobile ? '0 0.5rem' : '0 1rem',
          position: 'relative'
        }}>
          {/* Back Button - Far Left */}
          <button
            onClick={() => navigateTo('pharma-homepage')}
            style={{
              background: 'linear-gradient(135deg, #225fe1 0%, #1e40af 100%)',
              color: 'white',
              border: 'none',
              padding: isMobile ? '0.4rem' : '0.5rem',
              borderRadius: '8px',
              fontSize: isMobile ? '0.875rem' : '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(34, 95, 225, 0.3)',
              transition: 'all 0.3s ease',
              width: isMobile ? '32px' : '36px',
              height: isMobile ? '32px' : '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: '0',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (e.currentTarget) {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(34, 95, 225, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(34, 95, 225, 0.3)';
              }
            }}
          >
            ←
          </button>

          {/* Center Heading - Absolutely Centered */}
          <div style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: isMobile ? '1rem' : '1.5rem',
            fontWeight: '700',
            color: '#1B5E20',
            letterSpacing: '0.5px',
            textShadow: '0 2px 4px rgba(27, 94, 32, 0.1)',
            whiteSpace: 'nowrap',
            maxWidth: isMobile ? 'calc(100% - 80px)' : 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {isMobile ? 'INVENTORY' : 'INVENTORY MANAGEMENT'}
          </div>
        </div>
      </div>
      )}

      {/* Inventory Stats Component */}
      <InventoryStats
        medications={medications}
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        sortBy={sortBy}
        onSearchChange={setSearchTerm}
        onStatusFilterChange={setStatusFilter}
        onSortByChange={setSortBy}
      />

      {/* Loading State */}
      {isLoadingData && (
        <div style={{
          textAlign: 'center',
          padding: isMobile ? '2rem 1rem' : '4rem',
          color: '#6b7280'
        }}>
          <div style={{
            width: isMobile ? '32px' : '40px',
            height: isMobile ? '32px' : '40px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #10b981',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1.5rem auto'
          }}></div>
          <h3 style={{
            fontSize: isMobile ? '1rem' : '1.25rem',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '0.5rem'
          }}>
            Loading Inventory...
          </h3>
          <p style={{
            fontSize: isMobile ? '0.875rem' : '1rem',
            color: '#6b7280'
          }}>
            Please wait while we fetch the data
          </p>
        </div>
      )}

      {/* Error State */}
      {apiError && !isLoadingData && (
        <div style={{
          textAlign: 'center',
          padding: isMobile ? '2rem 1rem' : '4rem',
          color: '#6b7280'
        }}>
          <svg width={isMobile ? "48" : "64"} height={isMobile ? "48" : "64"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ marginBottom: '1.5rem', opacity: 0.5 }}>
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          <h3 style={{
            fontSize: isMobile ? '1rem' : '1.25rem',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '0.5rem',
            padding: isMobile ? '0 0.5rem' : '0',
            wordBreak: 'break-word'
          }}>
            {apiError}
          </h3>
          <button
            onClick={() => fetchInventoryData(pagination.page)}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              padding: isMobile ? '0.625rem 1.25rem' : '0.75rem 1.5rem',
              borderRadius: '8px',
              fontSize: isMobile ? '0.875rem' : '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              marginTop: '1rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              if (e.currentTarget) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Medications Table */}
      {!isLoadingData && !apiError && (
      <>
      <div style={{
        margin: isMobile ? '0 0.5rem 1rem 0.5rem' : '0 2rem 1rem 2rem',
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: isMobile ? '8px' : '12px',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        backdropFilter: 'blur(20px)',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 1
      }}>
        {isMobile ? (
          /* Mobile Card Layout */
          <div style={{ padding: '0.5rem' }}>
            {sortedMedications.map((medication, index) => (
              <div key={medication.id} style={{
                background: index % 2 === 0 ? 'rgba(255, 255, 255, 0.9)' : 'rgba(248, 250, 252, 0.9)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: index === sortedMedications.length - 1 ? '0' : '0.75rem',
                border: '1px solid rgba(226, 232, 240, 0.5)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
              }}>
                {/* Medicine Name - Header */}
                <div style={{
                  fontWeight: '700',
                  color: '#1B5E20',
                  fontSize: '1rem',
                  marginBottom: '0.5rem',
                  paddingBottom: '0.5rem',
                  borderBottom: '2px solid rgba(34, 197, 94, 0.2)'
                }}>
                  {typeof medication.medicine_name === 'string' ? medication.medicine_name : 'Unknown Medicine'}
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.75rem',
                  marginBottom: '0.75rem'
                }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>S.No.</div>
                    <div style={{ fontSize: '0.875rem', fontWeight: '700', color: '#374151' }}>
                      {index + 1}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Quantity</div>
                    <div style={{ fontSize: '0.875rem', fontWeight: '700', color: '#225fe1' }}>
                      {medication.quantity || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Type</div>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', textTransform: 'lowercase' }}>
                      {String(medication.raw_quantity_type ?? medication.quantity_type ?? '')}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>MRP</div>
                    <div style={{ fontSize: '0.875rem', fontWeight: '700', color: '#1B5E20' }}>
                      {medication.mrp ? `₹${medication.mrp.toFixed(2)}` : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Batch</div>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                      {typeof medication.batch_no === 'string' ? medication.batch_no : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Expiry</div>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                      {medication.expiry_date ? new Date(medication.expiry_date).toLocaleDateString('en-IN') : 'N/A'}
                    </div>
                  </div>
                </div>

                {/* Edit & Delete buttons - Mobile */}
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                  <button
                    type="button"
                    onClick={() => openEditModal(medication)}
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      color: '#1e40af',
                      background: 'rgba(30, 64, 175, 0.12)',
                      border: '1px solid rgba(30, 64, 175, 0.3)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirmItem(medication)}
                    disabled={deletingId === medication.id}
                    style={{
                      padding: '0.5rem 1rem',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      color: '#b91c1c',
                      background: 'rgba(185, 28, 28, 0.12)',
                      border: '1px solid rgba(185, 28, 28, 0.3)',
                      borderRadius: '8px',
                      cursor: deletingId === medication.id ? 'not-allowed' : 'pointer',
                      flex: 1
                    }}
                  >
                    {deletingId === medication.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}

            {/* Mobile Footer */}
            <div style={{
              padding: '1rem',
              background: 'rgba(248, 250, 252, 0.8)',
              borderTop: '1px solid rgba(226, 232, 240, 0.5)',
              marginTop: '0.5rem',
              borderRadius: '8px'
            }}>
              <div style={{
                fontSize: '0.875rem',
                fontWeight: '700',
                color: '#1B5E20',
                marginBottom: 0,
                textAlign: 'center'
              }}>
                Total Medications: {totalItems > 0 ? totalItems : sortedMedications.length}
              </div>
            </div>
          </div>
        ) : (
          /* Desktop Table Layout */
          <>
            {/* Table Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '60px 2fr 1fr 1fr 1fr 1fr 1fr 140px',
              gap: '1rem',
              padding: '1rem',
              background: 'linear-gradient(135deg, #225fe1 0%, #1e40af 100%)',
              color: 'white',
              fontWeight: '600',
              fontSize: '0.85rem',
              textAlign: 'center'
            }}>
              <div>S.No.</div>
              <div>Name</div>
              <div>Quantity</div>
              <div>Type</div>
              <div>Batch</div>
              <div>Expiry</div>
              <div>MRP</div>
              <div>Actions</div>
            </div>

            {/* Table Body */}
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {sortedMedications.map((medication, index) => (
                <div key={medication.id} style={{
                  display: 'grid',
                  gridTemplateColumns: '60px 2fr 1fr 1fr 1fr 1fr 1fr 140px',
                  gap: '1rem',
                  padding: '1rem',
                  borderBottom: index === sortedMedications.length - 1 ? 'none' : '1px solid rgba(226, 232, 240, 0.3)',
                  alignItems: 'center',
                  background: index % 2 === 0 ? 'rgba(255, 255, 255, 0.9)' : 'rgba(248, 250, 252, 0.9)'
                }}>
                  <div style={{ textAlign: 'center', fontWeight: '700', color: '#374151' }}>
                    {index + 1}
                  </div>
                  <div style={{ fontWeight: '700', color: '#1B5E20' }}>
                    {typeof medication.medicine_name === 'string' ? medication.medicine_name : 'Unknown Medicine'}
                  </div>

                  {/* Quantity */}
                  <div style={{ textAlign: 'center', fontWeight: '600', color: '#374151' }}>
                    {medication.quantity || 0}
                  </div>

                  <div style={{ textAlign: 'center', fontWeight: '600', color: '#374151', textTransform: 'lowercase' }}>
                    {String(medication.raw_quantity_type ?? medication.quantity_type ?? '')}
                  </div>
                  <div style={{ textAlign: 'center', fontWeight: '600', color: '#374151' }}>
                    {typeof medication.batch_no === 'string' ? medication.batch_no : 'N/A'}
                  </div>
                  <div style={{ textAlign: 'center', fontWeight: '600', color: '#374151' }}>
                    {medication.expiry_date ? new Date(medication.expiry_date).toLocaleDateString('en-IN') : 'N/A'}
                  </div>

                  {/* MRP */}
                  <div style={{ textAlign: 'center', fontWeight: '600', color: '#1B5E20' }}>
                    {medication.mrp ? `₹${medication.mrp.toFixed(2)}` : 'N/A'}
                  </div>

                  {/* Actions - Edit & Delete */}
                  <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => openEditModal(medication)}
                      style={{
                        padding: '0.35rem 0.6rem',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#1e40af',
                        background: 'rgba(30, 64, 175, 0.12)',
                        border: '1px solid rgba(30, 64, 175, 0.3)',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmItem(medication)}
                      disabled={deletingId === medication.id}
                      style={{
                        padding: '0.35rem 0.6rem',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#b91c1c',
                        background: 'rgba(185, 28, 28, 0.12)',
                        border: '1px solid rgba(185, 28, 28, 0.3)',
                        borderRadius: '6px',
                        cursor: deletingId === medication.id ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {deletingId === medication.id ? '...' : 'Delete'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Table Footer */}
            <div style={{
              padding: '1rem',
              background: 'rgba(248, 250, 252, 0.8)',
              borderTop: '1px solid rgba(226, 232, 240, 0.5)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#1B5E20'
            }}>
              <div>Total Medications: {totalItems > 0 ? totalItems : sortedMedications.length}</div>
            </div>
          </>
        )}
      </div>

      {totalPages > 0 && (
        <div style={{
          margin: isMobile ? '0 0.5rem 1.5rem 0.5rem' : '0 2rem 1.5rem 2rem',
          width: isMobile ? 'calc(100% - 1rem)' : 'calc(100% - 4rem)',
          maxWidth: '100%',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(25px)',
          borderRadius: '20px',
          padding: isMobile ? '1rem' : '1.5rem',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: isMobile ? '1rem' : '0',
          flexWrap: 'wrap'
        }}>
          <div style={{
            fontSize: isMobile ? '0.8rem' : '0.875rem',
            color: '#6b7280',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap',
            justifyContent: isMobile ? 'center' : 'flex-start'
          }}>
            <span>Showing</span>
            <span style={{ fontWeight: '700', color: '#10b981' }}>{showingFrom || 0}</span>
            <span>to</span>
            <span style={{ fontWeight: '700', color: '#10b981' }}>{showingTo || 0}</span>
            <span>of</span>
            <span style={{ fontWeight: '700', color: '#10b981' }}>{totalItems || sortedMedications.length}</span>
            <span>medicines</span>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            justifyContent: isMobile ? 'center' : 'flex-start',
            width: isMobile ? '100%' : 'auto'
          }}>
            <label style={{
              fontSize: isMobile ? '0.8rem' : '0.875rem',
              color: '#6b7280',
              fontWeight: '500',
              whiteSpace: 'nowrap'
            }}>
              Per page:
            </label>
            <select
              value={effectivePerPage}
              onChange={(e) => {
                const nextPerPage = Number(e.target.value) || 10;
                setPagination(prev => ({ ...prev, per_page: nextPerPage, page: 1 }));
                fetchInventoryData(1, nextPerPage);
              }}
              style={{
                padding: isMobile ? '0.625rem 0.75rem' : '0.5rem 0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: isMobile ? '0.8rem' : '0.875rem',
                fontWeight: '500',
                color: '#374151',
                background: 'white',
                cursor: 'pointer',
                outline: 'none',
                transition: 'all 0.2s ease',
                minWidth: isMobile ? '80px' : 'auto'
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
            </select>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap',
            justifyContent: isMobile ? 'center' : 'flex-end'
          }}>
            <button
              onClick={() => fetchInventoryData(Math.max(1, currentPage - 1))}
              disabled={!canPrevPage}
              style={{
                padding: isMobile ? '0.625rem 0.75rem' : '0.5rem 0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: isMobile ? '0.8rem' : '0.875rem',
                fontWeight: '600',
                color: !canPrevPage ? '#9ca3af' : '#374151',
                background: !canPrevPage ? '#f3f4f6' : 'white',
                cursor: !canPrevPage ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                minWidth: isMobile ? '100px' : 'auto'
              }}
              onMouseEnter={(e) => {
                if (canPrevPage && e.currentTarget) {
                  e.currentTarget.style.borderColor = '#10b981';
                  e.currentTarget.style.color = '#10b981';
                  e.currentTarget.style.background = '#f0fdf4';
                }
              }}
              onMouseLeave={(e) => {
                if (canPrevPage && e.currentTarget) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.color = '#374151';
                  e.currentTarget.style.background = 'white';
                }
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Previous
            </button>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              flexWrap: 'wrap'
            }}>
              {getPageNumbers().map((page, index) => (
                page === '...' ? (
                  <span
                    key={`ellipsis-${index}`}
                    style={{
                      padding: '0.5rem',
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      fontWeight: '600'
                    }}
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={page as number}
                    onClick={() => fetchInventoryData(page as number)}
                    style={{
                      minWidth: isMobile ? '36px' : '40px',
                      padding: isMobile ? '0.5rem 0.625rem' : '0.5rem 0.75rem',
                      border: `2px solid ${currentPage === page ? '#10b981' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: isMobile ? '0.8rem' : '0.875rem',
                      fontWeight: '600',
                      color: currentPage === page ? '#10b981' : '#374151',
                      background: currentPage === page ? '#f0fdf4' : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== page && e.currentTarget) {
                        e.currentTarget.style.borderColor = '#10b981';
                        e.currentTarget.style.color = '#10b981';
                        e.currentTarget.style.background = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== page && e.currentTarget) {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.color = '#374151';
                        e.currentTarget.style.background = 'white';
                      }
                    }}
                  >
                    {page}
                  </button>
                )
              ))}
            </div>

            <button
              onClick={() => fetchInventoryData(Math.min(totalPages, currentPage + 1))}
              disabled={!canNextPage}
              style={{
                padding: isMobile ? '0.625rem 0.75rem' : '0.5rem 0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: isMobile ? '0.8rem' : '0.875rem',
                fontWeight: '600',
                color: !canNextPage ? '#9ca3af' : '#374151',
                background: !canNextPage ? '#f3f4f6' : 'white',
                cursor: !canNextPage ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                minWidth: isMobile ? '100px' : 'auto'
              }}
              onMouseEnter={(e) => {
                if (canNextPage && e.currentTarget) {
                  e.currentTarget.style.borderColor = '#10b981';
                  e.currentTarget.style.color = '#10b981';
                  e.currentTarget.style.background = '#f0fdf4';
                }
              }}
              onMouseLeave={(e) => {
                if (canNextPage && e.currentTarget) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.color = '#374151';
                  e.currentTarget.style.background = 'white';
                }
              }}
            >
              Next
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      )}
      </>
      )}

      {/* Edit Inventory Modal */}
      {editingItem && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }} onClick={(e) => e.target === e.currentTarget && closeEditModal()}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '560px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            padding: '1.5rem'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', color: '#1B5E20' }}>Edit Inventory</h3>
            {editError && (
              <div style={{ padding: '0.5rem', marginBottom: '1rem', background: '#fef2f2', color: '#b91c1c', borderRadius: '8px', fontSize: '0.875rem' }}>
                {editError}
              </div>
            )}
            {isLoadingEditDetails && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: '0.75rem',
                padding: '2rem 1rem',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                borderRadius: '12px',
                background: 'rgba(248, 250, 252, 0.9)',
                marginBottom: '1rem'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #e5e7eb',
                  borderTop: '4px solid #10b981',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#374151' }}>Loading details...</div>
              </div>
            )}
            {!isLoadingEditDetails && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '0.75rem 1rem',
              alignItems: 'start'
            }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Medicine Name</label>
                <input
                  value={editForm.medicine_name}
                  onChange={(e) => setEditForm(f => ({ ...f, medicine_name: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                  placeholder="Medicine name"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Medicine Company</label>
                <input
                  value={editForm.medicine_company}
                  onChange={(e) => setEditForm(f => ({ ...f, medicine_company: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                  placeholder="Company"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Medicine Composition</label>
                <input
                  value={editForm.medicine_composition}
                  onChange={(e) => setEditForm(f => ({ ...f, medicine_composition: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                  placeholder="Composition"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Batch No</label>
                <input
                  value={editForm.batch_no}
                  onChange={(e) => setEditForm(f => ({ ...f, batch_no: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                  placeholder="Batch number"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Expiry Date</label>
                <input
                  type="date"
                  value={editForm.expiry_date}
                  onChange={(e) => setEditForm(f => ({ ...f, expiry_date: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Quantity</label>
                <input
                  type="number"
                  value={editForm.quantity || ''}
                  onChange={(e) => setEditForm(f => ({ ...f, quantity: Number(e.target.value) || 0 }))}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                  placeholder="Quantity"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Quantity Type</label>
                <select
                  value={editForm.quantity_type}
                  onChange={(e) => {
                    const next = e.target.value;
                    const nextNormalized = normalizeQtyTypeToApi(next);
                    setEditForm(f => ({
                      ...f,
                      quantity_type: nextNormalized,
                      ...(nextNormalized !== 'strip' ? { tablets_per_strip: '', fragmented_tablets: '' } : {})
                    }));
                  }}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', background: 'white' }}
                >
                  {qtyTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Tablets Per Strip (only for Strip)</label>
                <input
                  type="number"
                  value={editForm.tablets_per_strip}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEditForm(f => ({ ...f, tablets_per_strip: value === '' ? '' : Number(value) }));
                  }}
                  disabled={normalizeQtyTypeToApi(editForm.quantity_type) !== 'strip'}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', backgroundColor: normalizeQtyTypeToApi(editForm.quantity_type) !== 'strip' ? '#f9fafb' : 'white' }}
                  placeholder="e.g. 10"
                  min={1}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Fragmented Tablets (only for Strip)</label>
                <input
                  type="number"
                  value={editForm.fragmented_tablets}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEditForm(f => ({ ...f, fragmented_tablets: value === '' ? '' : Number(value) }));
                  }}
                  disabled={normalizeQtyTypeToApi(editForm.quantity_type) !== 'strip'}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box', backgroundColor: normalizeQtyTypeToApi(editForm.quantity_type) !== 'strip' ? '#f9fafb' : 'white' }}
                  placeholder="e.g. 2"
                  min={0}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Unit Cost</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.unit_cost || ''}
                  onChange={(e) => setEditForm(f => ({ ...f, unit_cost: Number(e.target.value) || 0 }))}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                  placeholder="Unit cost"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>MRP</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.mrp || ''}
                  onChange={(e) => {
                    const newMrp = Number(e.target.value) || 0;
                    setEditForm(f => ({ 
                      ...f, 
                      mrp: newMrp
                    }));
                  }}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                  placeholder="MRP"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Selling Price</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.selling_price || ''}
                  onChange={(e) => {
                    const newSellingPrice = Number(e.target.value) || 0;
                    setEditForm(f => ({ 
                      ...f, 
                      selling_price: newSellingPrice
                    }));
                    // Mark as manually edited once user types something
                    setSellingPriceManuallyEdited(true);
                  }}
                  onFocus={(e) => {
                    // Only auto-fill if: not manually edited, selling price is 0, and MRP exists
                    if (!sellingPriceManuallyEdited && editForm.selling_price === 0 && editForm.mrp > 0) {
                      setEditForm(f => ({ 
                        ...f, 
                        selling_price: Number(editForm.mrp)
                      }));
                    }
                  }}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                  placeholder="Selling price (auto-fills from MRP)"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>HSN Code</label>
                <input
                  value={editForm.hsn_code}
                  onChange={(e) => setEditForm(f => ({ ...f, hsn_code: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                  placeholder="HSN code"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Storage Location</label>
                <input
                  value={editForm.storage_location}
                  onChange={(e) => setEditForm(f => ({ ...f, storage_location: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                  placeholder="Storage location"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>GST %</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.gst_percent || ''}
                  onChange={(e) => setEditForm(f => ({ ...f, gst_percent: Number(e.target.value) || 0 }))}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box' }}
                  placeholder="GST %"
                />
              </div>
            </div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
              {!isLoadingEditDetails && (
                <button
                  type="button"
                  onClick={handleEditSubmit}
                  disabled={isSubmittingEdit}
                  style={{
                    flex: 1,
                    padding: '0.6rem',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: isSubmittingEdit ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSubmittingEdit ? (isDoctorAssociated ? 'Requesting...' : 'Saving...') : (isDoctorAssociated ? 'Request Save' : 'Save')}
                </button>
              )}
              <button
                type="button"
                onClick={closeEditModal}
                disabled={isSubmittingEdit}
                style={{
                  flex: 1,
                  padding: '0.6rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: isSubmittingEdit ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation popup */}
      {deleteConfirmItem && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={(e) => e.target === e.currentTarget && setDeleteConfirmItem(null)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              padding: '1.5rem'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1.125rem', color: '#1f2937', fontWeight: '600' }}>
              Delete from inventory?
            </h3>
            <p style={{ margin: '0 0 1.25rem 0', fontSize: '0.9375rem', color: '#6b7280', lineHeight: 1.5 }}>
              Are you sure you want to delete{' '}
              <strong style={{ color: '#1f2937' }}>
                {typeof deleteConfirmItem.medicine_name === 'string' ? deleteConfirmItem.medicine_name : 'this item'}
              </strong>{' '}
              from inventory? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setDeleteConfirmItem(null)}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#374151',
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteConfirmItem && handleDeleteItem(deleteConfirmItem)}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: 'white',
                  background: '#b91c1c',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Animated Notification */}
      {notification && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            transform: notification.visible ? 'translateX(0)' : 'translateX(120%)',
            opacity: notification.visible ? 1 : 0,
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          }}
        >
          <div
            style={{
              background: notification.type === 'success' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              padding: '1rem 1.25rem',
              borderRadius: '12px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              minWidth: '300px',
              maxWidth: '400px',
            }}
          >
            <div
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {notification.type === 'success' ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20,6 9,17 4,12" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                {notification.type === 'success' ? 'Success!' : 'Error!'}
              </div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9, marginTop: '2px' }}>
                {notification.message}
              </div>
            </div>
            <button
              onClick={() => setNotification(prev => prev ? { ...prev, visible: false } : null)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '6px',
                padding: '4px 8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: '600',
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default Inventory;
